import { createRoute } from '@tanstack/react-router'
import { isUuid } from '@/lib/entity'
import { lazyPage } from '@/lib/lazy-page'
import {
  groupMembersGridContract,
  groupsGridContract,
  normalizeDataGridUrlState,
  oauthApplicationsGridContract,
  pendingUsersGridContract,
  roleAssignmentsGridContract,
  rolesGridContract,
  serviceAccountsGridContract,
  usersGridContract,
} from '@/modules/data-grid'
import { accessRoute } from './access-route'
import { userManagementScopeRoute } from './user-management-scope-route'

export const AccessHierarchy = lazyPage(() => import('./features/access-hierarchy/entity-hierarchy.lazy'), 'AccessHierarchy')
const EntityHierarchyRoute = lazyPage(() => import('./features/access-hierarchy/entity-hierarchy.lazy'), 'EntityHierarchyRoute')

type EntityHierarchySearch = { focus?: string }

export const entityHierarchyRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'hierarchy',
  validateSearch: (search: Record<string, unknown>): EntityHierarchySearch => ({
    focus:
      typeof search.focus === 'string' && isUuid(search.focus)
        ? search.focus
        : undefined,
  }),
  component: EntityHierarchyRoute,
})

export const EntityGroups = lazyPage(() => import('./features/groups/entity-groups.lazy'), 'EntityGroups')

export type EntityGroupsSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeEntityGroupsSearch(search: Record<string, unknown>): EntityGroupsSearch {
  return normalizeDataGridUrlState(search, groupsGridContract)
}

export const entityGroupsRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'groups',
  validateSearch: decodeEntityGroupsSearch,
  component: EntityGroups,
  staticData: { title: 'Groups' },
})

export const GroupDetail = lazyPage(() => import('./features/groups/group-detail.lazy'), 'GroupDetail')

export type GroupDetailSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeGroupDetailSearch(search: Record<string, unknown>): GroupDetailSearch {
  return normalizeDataGridUrlState(search, groupMembersGridContract)
}

export const groupDetailRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'groups/$groupId',
  validateSearch: decodeGroupDetailSearch,
  component: GroupDetail,
})

export const NewGroup = lazyPage(() => import('./features/groups/new-group.lazy'), 'NewGroup')

export const newGroupRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'groups/new',
  component: NewGroup,
})

export const EntityRoles = lazyPage(() => import('./features/roles/entity-roles.lazy'), 'EntityRoles')

export type EntityRolesSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeEntityRolesSearch(search: Record<string, unknown>): EntityRolesSearch {
  return normalizeDataGridUrlState(search, rolesGridContract)
}

export const entityRolesRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'roles',
  validateSearch: decodeEntityRolesSearch,
  component: EntityRoles,
  staticData: { title: 'Roles' },
})

export const RoleDetail = lazyPage(() => import('./features/roles/role-detail.lazy'), 'RoleDetail')

export type RoleDetailSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeRoleDetailSearch(search: Record<string, unknown>): RoleDetailSearch {
  return normalizeDataGridUrlState(search, roleAssignmentsGridContract)
}

export const roleDetailRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'roles/$roleId',
  validateSearch: decodeRoleDetailSearch,
  component: RoleDetail,
  staticData: { title: 'Role' },
})

export const EntityPendingUsers = lazyPage(() => import('./features/pending-users/entity-pending-users.lazy'), 'EntityPendingUsers')

export type EntityPendingUsersSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeEntityPendingUsersSearch(search: Record<string, unknown>): EntityPendingUsersSearch {
  return normalizeDataGridUrlState(search, pendingUsersGridContract)
}

export const entityPendingUsersRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'pending-users',
  validateSearch: decodeEntityPendingUsersSearch,
  component: EntityPendingUsers,
})

export const EntityUsers = lazyPage(() => import('./features/users/entity-users.lazy'), 'EntityUsers')

export type EntityUsersSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
  includeDeactivated?: boolean
}

export function decodeEntityUsersSearch(search: Record<string, unknown>): EntityUsersSearch {
  return normalizeDataGridUrlState(search, usersGridContract)
}

export const entityUsersRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'users',
  validateSearch: decodeEntityUsersSearch,
  component: EntityUsers,
  staticData: { title: 'Users' },
})

export const ImportUsers = lazyPage(() => import('./features/provisioning/import-users.lazy'), 'ImportUsers')

export const importUsersRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'users/import',
  component: ImportUsers,
})

export const Provisioning = lazyPage(() => import('./features/provisioning/provisioning.lazy'), 'Provisioning')

export const provisioningRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'provisioning',
  component: Provisioning,
  staticData: { title: 'Provisioning' },
})

const ServiceAccountDetailRoute = lazyPage(() => import('./features/service-accounts/service-account-detail.lazy'), 'ServiceAccountDetailRoute')

export const serviceAccountDetailRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'service-accounts/$userId',
  component: ServiceAccountDetailRoute,
})

const NewServiceAccountRoute = lazyPage(() => import('./features/service-accounts/new-service-account.lazy'), 'NewServiceAccountRoute')

export const newServiceAccountRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'service-accounts/new',
  component: NewServiceAccountRoute,
})

const ServiceAccountsRoute = lazyPage(() => import('./features/service-accounts/service-accounts.lazy'), 'ServiceAccountsRoute')

export type ServiceAccountsSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeServiceAccountsSearch(search: Record<string, unknown>): ServiceAccountsSearch {
  return normalizeDataGridUrlState(search, serviceAccountsGridContract)
}

export const serviceAccountsRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'service-accounts',
  validateSearch: decodeServiceAccountsSearch,
  component: ServiceAccountsRoute,
})

export const UserAccessProfile = lazyPage(() => import('./features/users/user-access-profile.lazy'), 'UserAccessProfile')

export const userAccessProfileRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'users/$userId',
  component: UserAccessProfile,
})

const OAuthApplicationsRoute = lazyPage(() => import('./features/oauth-applications/oauth-applications.lazy'), 'OAuthApplicationsRoute')

export type OAuthApplicationsSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeOAuthApplicationsSearch(search: Record<string, unknown>): OAuthApplicationsSearch {
  return normalizeDataGridUrlState(search, oauthApplicationsGridContract)
}

export const oauthApplicationsRoute = createRoute({
  getParentRoute: () => accessRoute,
  path: 'oauth-applications',
  validateSearch: decodeOAuthApplicationsSearch,
  component: OAuthApplicationsRoute,
})

const NewOAuthApplicationRoute = lazyPage(() => import('./features/oauth-applications/new-oauth-application.lazy'), 'NewOAuthApplicationRoute')

export const newOAuthApplicationRoute = createRoute({
  getParentRoute: () => accessRoute,
  path: 'oauth-applications/new',
  component: NewOAuthApplicationRoute,
})

const OAuthApplicationDetailRoute = lazyPage(() => import('./features/oauth-applications/oauth-application-detail.lazy'), 'OAuthApplicationDetailRoute')

export const oauthApplicationDetailRoute = createRoute({
  getParentRoute: () => accessRoute,
  path: 'oauth-applications/$applicationId',
  component: OAuthApplicationDetailRoute,
})
